import React, { useState } from "react";
import api from "../Service/api";
import "./Purchases.css";

const CompanyPayments = ({ data }) => {
  const [editing, setEditing] = useState(null);
  const [newAmount, setNewAmount] = useState(0);
  const [newDescription, setNewDescription] = useState("");
  const [newPaymentDate, setNewPaymentDate] = useState("");
  const [searchTerm, setSearchTerm] = useState(""); // 👈 new state

  // Helper functions
  const getDisplayInvoiceNumber = (payment) => {
    if (!payment.invoiceNumber || payment.invoiceNumber === 0 || payment.invoiceNumber === "0") {
      return payment.vendorInvoice || '-';
    }
    return payment.invoiceNumber;
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '-';
    const dt = new Date(dateStr);
    return dt.toLocaleDateString();
  };

  const formatDateTimeForAPI = (dateStr) => {
    if (!dateStr) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day} 00:00:00`;
    }
    let cleanDate = dateStr.replace('T', ' ').trim();
    if (!cleanDate.includes(' ')) {
      cleanDate = `${cleanDate} 00:00:00`;
    } else {
      const parts = cleanDate.split(' ');
      cleanDate = parts[0] + ' ' + parts[1];
    }
    return cleanDate;
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const dt = new Date(dateStr);
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filter data based on search term
  const filteredData = data
    ? Object.fromEntries(
        Object.entries(data).filter(([vendorName]) =>
          vendorName.toLowerCase().includes(searchTerm.toLowerCase().trim())
        )
      )
    : {};

  // Calculations – use filteredData
  const calculateGrandTotal = () => {
    if (!filteredData) return 0;
    return Object.values(filteredData).reduce((total, payments) => {
      const safePayments = Array.isArray(payments) ? payments : [];
      return total + safePayments.reduce((sum, p) => sum + Number(p.amountPaid || 0), 0);
    }, 0);
  };

  const calculateVendorsWithPayments = () => {
    if (!filteredData) return 0;
    return Object.values(filteredData).filter(payments => {
      const safePayments = Array.isArray(payments) ? payments : [];
      return safePayments.length > 0;
    }).length;
  };

  const calculateTotalPayments = () => {
    if (!filteredData) return 0;
    return Object.values(filteredData).reduce((total, payments) => {
      const safePayments = Array.isArray(payments) ? payments : [];
      return total + safePayments.length;
    }, 0);
  };

  // ==================== UPDATE ====================
  const handleUpdate = async (record) => {
    if (newAmount <= 0) return alert("Enter a valid amount");
    try {
      const payload = {
        invoiceNumber: record.invoiceNumber || 0,
        status: "Purchase",
        paymentTime: formatDateTimeForAPI(record.paymentTime),
        vendorName: record.vendorName,
        newAmountPaid: newAmount,
        description: newDescription,
        paymentDateStr: newPaymentDate,
      };
      if (record.vendorInvoice) {
        payload.vendorInvoiceNumber = record.vendorInvoice;
      }
      const res = await api.put("/billPayment/update-companyPayBill", payload);
      if (res.data.code === 1 || res.data.success) {
        alert(res.data.message || "Payment updated successfully");
        setEditing(null);
        setNewDescription("");
        setNewPaymentDate("");
        window.location.reload();
      } else {
        alert(res.data.message || "Update failed");
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Update failed! Check console.");
    }
  };

  // ==================== DELETE ====================
  const handleDelete = async (record) => {
    const displayId = record.vendorInvoice || record.invoiceNumber;
    if (!window.confirm(`Are you sure you want to delete payment ${displayId}?`)) return;
    try {
      const payload = {
        invoiceNumber: record.invoiceNumber || 0,
        status: "Purchase",
        paymentTime: formatDateTimeForAPI(record.paymentTime),
        vendorName: record.vendorName,
      };
      if (record.vendorInvoice) {
        payload.vendorInvoiceNumber = record.vendorInvoice;
      }
      const res = await api.delete("/billPayment/delete-companyPayBill", {
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.data.code === 1 || res.data.success) {
        alert(res.data.message || "Payment deleted successfully");
        window.location.reload();
      } else {
        alert(res.data.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Delete failed! Check console.");
    }
  };

  // ==================== PRINT ====================
  const handlePrint = () => {
    if (!filteredData || Object.keys(filteredData).length === 0) {
      alert("No data to print");
      return;
    }

    const vendorLatestTimes = {};
    Object.entries(filteredData).forEach(([vendorName, payments]) => {
      const safePayments = Array.isArray(payments) ? payments : [];
      if (safePayments.length > 0) {
        vendorLatestTimes[vendorName] = Math.max(
          ...safePayments.map(p => new Date(p.paymentTime).getTime())
        );
      } else {
        vendorLatestTimes[vendorName] = 0;
      }
    });

    const printWindow = window.open('', '_blank', 'width=800,height=600');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Company Payments Report</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; color: #000; line-height: 1.4; }
          .print-header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 15px; }
          .print-header h1 { margin-bottom: 10px; color: #000; }
          .date-range { font-size: 16px; margin-bottom: 5px; color: #000; }
          .generated-date { font-size: 14px; color: #000; }
          .vendor-section { margin-bottom: 30px; page-break-inside: avoid; }
          .vendor-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 2px solid #000; }
          .vendor-name { font-size: 18px; font-weight: bold; color: #000; }
          .vendor-total { font-weight: bold; font-size: 16px; color: #000; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
          th { background-color: #f2f2f2; font-weight: bold; text-align: left; }
          th, td { border: 1px solid #000; padding: 8px; color: #000; }
          .highlight-row { background-color: #e6f7ff; }
          .negative-amount { color: #ff0000 !important; }
          .latest-note { font-size: 12px; text-align: right; color: #666; margin-top: 5px; }
          .grand-total-section { margin-top: 30px; padding: 15px; border-top: 2px solid #000; border-bottom: 2px solid #000; background-color: #f8f8f8; }
          .grand-total-row { display: flex; justify-content: space-between; font-size: 18px; font-weight: bold; }
          .footer { margin-top: 40px; font-size: 12px; color: #666; text-align: center; }
          @media print { body { padding: 15px; } @page { margin: 0.5in; } }
        </style>
      </head>
      <body>
        <div class="print-header">
          <h1>Company Payments Report</h1>
          <p class="date-range">Filtered by: ${searchTerm || "All Vendors"}</p>
          <p class="generated-date">Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
        </div>

        ${Object.entries(filteredData).map(([vendorName, payments]) => {
          const safePayments = Array.isArray(payments) ? payments : [];
          const latestPaymentTime = vendorLatestTimes[vendorName];
          const totalPaid = safePayments.reduce((sum, p) => sum + Number(p.amountPaid || 0), 0);

          if (safePayments.length === 0) return '';

          return `
            <div class="vendor-section">
              <div class="vendor-header">
                <div class="vendor-name">Vendor: ${vendorName}</div>
                <div class="vendor-total">Total Paid: Rs ${totalPaid.toLocaleString()}</div>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Invoice No</th>
                    <th>Amount Paid</th>
                    <th>Description</th>
                    <th>Payment Date</th>
                    <th>Billing Month</th>
                  </tr>
                </thead>
                <tbody>
                  ${safePayments.map(p => {
                    const isLatest = new Date(p.paymentTime).getTime() === latestPaymentTime;
                    const rowClass = isLatest ? 'highlight-row' : '';
                    const amountClass = Number(p.amountPaid) < 0 ? 'negative-amount' : '';
                    const fontWeight = isLatest ? 'font-weight: bold;' : '';

                    const displayInvoice = !p.invoiceNumber || p.invoiceNumber === 0 || p.invoiceNumber === "0"
                      ? (p.vendorInvoice || '-')
                      : p.invoiceNumber;

                    const paymentDate = p.paymentTime ? new Date(p.paymentTime).toLocaleDateString() : '-';

                    return `
                      <tr class="${rowClass}">
                        <td>${displayInvoice}</td>
                        <td style="${fontWeight}" class="${amountClass}">Rs ${Number(p.amountPaid).toLocaleString()}</td>
                        <td>${p.description || '-'}</td>
                        <td>${paymentDate}</td>
                        <td>${p.billingMonth}</td>
                      </tr>
                    `;
                  }).join('')}
                </tbody>
              </table>

              ${safePayments.some(p => new Date(p.paymentTime).getTime() === latestPaymentTime) ? `
                <div class="latest-note">* Latest payment highlighted</div>
              ` : ''}
            </div>
          `;
        }).join('')}

        <div class="grand-total-section">
          <div class="grand-total-row">
            <span>GRAND TOTAL:</span>
            <span>Rs ${calculateGrandTotal().toLocaleString()}</span>
          </div>
        </div>

        <div class="footer">
          <p>--- End of Report ---</p>
          <p>Total Vendors with Payments: ${calculateVendorsWithPayments()}</p>
          <p>Total Payments: ${calculateTotalPayments()}</p>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); setTimeout(function() { window.close(); }, 500); }, 250);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const hasActualPaymentData = () => {
    if (!filteredData) return false;
    return Object.values(filteredData).some(payments => {
      const safePayments = Array.isArray(payments) ? payments : [];
      return safePayments.length > 0;
    });
  };

  if (!data) return <div>No data available</div>;

  if (!hasActualPaymentData()) {
    return (
      <div className="purchase-container">
        <div style={{ marginBottom: 20 }}><h1 style={{ margin: 0 }}>Company Payments</h1></div>
        {/* Search bar always visible */}
        <div style={{ marginBottom: 20, display: "flex", gap: "10px", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search by vendor name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ccc",
              borderRadius: 4,
              flex: 1,
              maxWidth: 300,
              fontSize: 14,
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                padding: "8px 16px",
                backgroundColor: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          )}
        </div>
        <div style={{ marginBottom: 20, textAlign: "right" }}>
          <button disabled style={{ padding: "8px 20px", backgroundColor: "#9ca3af", color: "white", border: "none", borderRadius: 6, cursor: "not-allowed", opacity: 0.6 }}>📄 Print Report</button>
        </div>
        <div style={{ textAlign: "center", padding: "60px 20px", backgroundColor: "#fefce8", borderRadius: 8, border: "1px solid #fde047", marginTop: 20 }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>📭</div>
          <h3 style={{ color: "#854d0e", marginBottom: 10 }}>
            {searchTerm ? `No payments found for "${searchTerm}"` : "No Payment Records Found"}
          </h3>
          <p style={{ color: "#a16207", marginBottom: 20 }}>
            {searchTerm ? "Try adjusting your search term." : "There are no company payment records available."}
          </p>
          <p style={{ color: "#ca8a04", fontSize: 14 }}>Total vendors in system: {Object.keys(data || {}).length}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="purchase-container">
      <div style={{ marginBottom: 20 }}><h1 style={{ margin: 0 }}>Company Payments</h1></div>

      {/* Search Bar */}
      <div style={{ marginBottom: 20, display: "flex", gap: "10px", alignItems: "center" }}>
        <input
          type="text"
          placeholder="Search by vendor name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: "8px 12px",
            border: "1px solid #ccc",
            borderRadius: 4,
            flex: 1,
            maxWidth: 300,
            fontSize: 14,
          }}
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            style={{
              padding: "8px 16px",
              backgroundColor: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 14, color: "#475569" }}>
          {Object.keys(filteredData).length} vendor{Object.keys(filteredData).length !== 1 ? "s" : ""} found
        </div>
      </div>

      <div style={{ marginBottom: 20, textAlign: "right" }}>
        <button onClick={handlePrint} style={{ padding: "8px 20px", backgroundColor: "#3b82f6", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 500, fontSize: 14 }}>📄 Print Report</button>
      </div>

      {Object.entries(filteredData).map(([vendorName, payments]) => {
        const safePayments = Array.isArray(payments) ? payments : [];
        const latestPaymentTime = safePayments.length > 0 ? Math.max(...safePayments.map(p => new Date(p.paymentTime).getTime())) : 0;
        const totalPaid = safePayments.reduce((sum, p) => sum + Number(p.amountPaid || 0), 0);

        if (safePayments.length === 0) return null;

        return (
          <div className="purchase-table-container" key={vendorName} style={{ background: "#f9fafb", boxShadow: "0 4px 10px rgba(0,0,0,0.1)", marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <h3 className="invoice-title">{vendorName}</h3>
              <div style={{ fontWeight: "bold", color: "#0c6303", fontSize: "16px" }}>Total Paid: Rs {totalPaid.toLocaleString()}</div>
            </div>

            <div className="table-scroll-wrapper">
              <table className="purchase-table">
                <thead style={{ background: "#e5e7eb" }}>
                  <tr>
                    <th>Invoice No</th>
                    <th>Amount Paid</th>
                    <th>Description</th>
                    <th>Payment Date</th>
                    <th>Billing Month</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {safePayments.map((p) => {
                    const isLatest = new Date(p.paymentTime).getTime() === latestPaymentTime;
                    const isEditingCurrent = editing === p.id;
                    const displayInvoice = getDisplayInvoiceNumber(p);
                    const amountColor = "#000000";

                    return (
                      <tr key={p.id} style={{ background: isLatest ? "#ecfdf5" : "transparent" }}>
                        <td style={{ color: "#000000" }}>{displayInvoice}</td>

                        <td style={{ color: amountColor, fontWeight: isLatest ? "bold" : "normal" }}>
                          {isEditingCurrent ? (
                            <input
                              type="number"
                              value={newAmount}
                              onChange={(e) => setNewAmount(parseFloat(e.target.value) || 0)}
                              style={{ width: "80px", color: "#000", backgroundColor: "#fff", border: "1px solid #ccc", padding: "2px 4px", fontSize: "14px" }}
                            />
                          ) : (
                            `Rs ${Number(p.amountPaid).toLocaleString()}`
                          )}
                        </td>

                        <td style={{ color: "#000000" }}>
                          {isEditingCurrent ? (
                            <input
                              type="text"
                              value={newDescription}
                              onChange={(e) => setNewDescription(e.target.value)}
                              placeholder="Enter description"
                              style={{ width: "150px", color: "#000", backgroundColor: "#fff", border: "1px solid #ccc", padding: "2px 4px", fontSize: "14px" }}
                            />
                          ) : (
                            p.description || '-'
                          )}
                        </td>

                        <td style={{ color: "#000000" }}>
                          {isEditingCurrent ? (
                            <input
                              type="date"
                              value={newPaymentDate}
                              onChange={(e) => setNewPaymentDate(e.target.value)}
                              style={{ color: "#000", backgroundColor: "#fff", border: "1px solid #ccc", padding: "2px 4px", fontSize: "14px" }}
                            />
                          ) : (
                            formatDateOnly(p.paymentTime)
                          )}
                        </td>

                        <td style={{ color: "#000000" }}>{p.billingMonth}</td>

                        <td style={{ display: "flex", gap: "8px" }}>
                          {isEditingCurrent ? (
                            <>
                              <button style={{ padding: "4px 8px", fontSize: "12px", marginRight: "4px", cursor: "pointer", backgroundColor: "#265e3b", color: "white", border: "none", borderRadius: 4 }} onClick={() => handleUpdate(p)}>Save</button>
                              <button style={{ padding: "4px 8px", fontSize: "12px", cursor: "pointer", backgroundColor: "#ef4444", color: "white", border: "none", borderRadius: 4 }} onClick={() => { setEditing(null); setNewAmount(0); setNewDescription(""); setNewPaymentDate(""); }}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button style={{ padding: "4px 8px", fontSize: "12px", marginRight: "4px", cursor: "pointer", backgroundColor: "#16a34a", color: "white", border: "none", borderRadius: 4 }} onClick={() => { 
                                setEditing(p.id); 
                                setNewAmount(p.amountPaid); 
                                setNewDescription(p.description || ""); 
                                setNewPaymentDate(formatDateForInput(p.paymentTime)); 
                              }}>Update</button>
                              <button style={{ padding: "4px 8px", fontSize: "12px", cursor: "pointer", backgroundColor: "#ef4444", color: "white", border: "none", borderRadius: 4 }} onClick={() => handleDelete(p)}>Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: "10px", fontSize: "13px", color: "#065f46", textAlign: "right" }}>Latest payment highlighted</div>
          </div>
        );
      })}

      {hasActualPaymentData() && (
        <div style={{ marginTop: 30, padding: 20, backgroundColor: "#f0f9ff", borderRadius: 8, border: "2px solid #0ea5e9" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 18, fontWeight: "bold" }}>
            <span style={{ color: "#0369a1" }}>GRAND TOTAL:</span>
            <span style={{ color: "#0c6303", fontSize: 20 }}>Rs {calculateGrandTotal().toLocaleString()}</span>
          </div>
          <div style={{ marginTop: 10, fontSize: 14, color: "#475569", display: "flex", justifyContent: "space-between" }}>
            <span>Total Vendors with Payments: {calculateVendorsWithPayments()}</span>
            <span>Total Payments: {calculateTotalPayments()}</span>
          </div>
          <div style={{ marginTop: 5, fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>
            Total vendors in system: {Object.keys(data || {}).length}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPayments;